import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Search, Loader2, CheckCircle2, AlertCircle, Info } from "lucide-react";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import CtaBand from "@/components/CtaBand";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { SITE } from "@/data/site";

const TIER_STYLES = {
  primary: { Icon: CheckCircle2, cls: "border-amber-400/40 bg-amber-400/10 text-amber-300" },
  extended: { Icon: Info, cls: "border-blue-400/30 bg-blue-400/10 text-blue-200" },
  outside: { Icon: AlertCircle, cls: "border-neutral-500/30 bg-white/5 text-neutral-200" },
  unknown: { Icon: Info, cls: "border-neutral-500/30 bg-white/5 text-neutral-200" },
};

export default function ServiceArea() {
  const [zip, setZip] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const check = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    if (!/^\d{5}$/.test(zip.trim())) {
      setError("Enter a valid 5-digit ZIP code.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/service-area/check`, { params: { zip: zip.trim() } });
      setResult(data);
    } catch (err) {
      setError(formatApiError(err, "Couldn't check that ZIP right now."));
    } finally {
      setLoading(false);
    }
  };

  const tier = result ? TIER_STYLES[result.tier] || TIER_STYLES.unknown : null;

  return (
    <>
      <Seo
        title="Service Area"
        description="Kensmarc Electric is based near 91702 and serves surrounding communities within about a 25-mile radius, with projects across Los Angeles County depending on location."
        path="/service-area"
      />
      <PageHeader
        eyebrow="Coverage"
        title="Service area"
        subtitle={SITE.serviceAreaBlurb}
      />

      <section className="section-pad">
        <div className="container-kx grid gap-10 lg:grid-cols-2 lg:items-start">
          <div>
            <h2 className="font-display text-2xl font-bold uppercase text-white">Where we work</h2>
            <ul className="mt-5 space-y-4">
              <li className="flex gap-4 rounded-xl border border-[#20202a] bg-[#101017] p-5">
                <MapPin className="mt-0.5 h-6 w-6 shrink-0 text-amber-400" />
                <div>
                  <p className="font-semibold text-white">Primary area — {SITE.serviceZip}</p>
                  <p className="mt-1 text-sm text-neutral-400">Based near the {SITE.serviceZip} area, serving the surrounding communities.</p>
                </div>
              </li>
              <li className="flex gap-4 rounded-xl border border-[#20202a] bg-[#101017] p-5">
                <MapPin className="mt-0.5 h-6 w-6 shrink-0 text-amber-400" />
                <div>
                  <p className="font-semibold text-white">Standard coverage — ~{SITE.radiusMiles} miles</p>
                  <p className="mt-1 text-sm text-neutral-400">Normal coverage extends approximately {SITE.radiusMiles} miles from {SITE.serviceZip}.</p>
                </div>
              </li>
              <li className="flex gap-4 rounded-xl border border-[#20202a] bg-[#101017] p-5">
                <MapPin className="mt-0.5 h-6 w-6 shrink-0 text-amber-400" />
                <div>
                  <p className="font-semibold text-white">Broader projects — {SITE.county}</p>
                  <p className="mt-1 text-sm text-neutral-400">
                    We handle select projects across {SITE.county} depending on location and project
                    requirements. Not every location is automatically within the standard radius — reach out
                    and we'll confirm.
                  </p>
                </div>
              </li>
            </ul>
          </div>

          {/* ZIP checker */}
          <div className="rounded-2xl border border-[#22222c] bg-[#101017] p-6 sm:p-8">
            <h2 className="font-display text-2xl font-bold uppercase text-white">Check your ZIP code</h2>
            <p className="mt-2 text-sm text-neutral-400">
              Enter your ZIP to see how it lines up with our service area. Outside the radius? You can still
              request a quote.
            </p>
            <form onSubmit={check} className="mt-5 flex gap-3" noValidate>
              <Input
                inputMode="numeric"
                maxLength={5}
                value={zip}
                onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
                placeholder="e.g. 91702"
                className="bg-[#0f0f15] border-[#2a2a34] text-white placeholder:text-neutral-500 focus-visible:ring-amber-400/60"
                data-testid="service-area-zip-input"
                aria-label="ZIP code"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="service-area-check-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-amber-400 px-5 font-bold uppercase tracking-wide text-[#0A0A0C] hover:bg-amber-300 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Check
              </button>
            </form>

            {error && <p className="mt-4 text-sm text-red-400" data-testid="service-area-error">{error}</p>}

            {result && tier && (
              <div className={`mt-5 flex items-start gap-3 rounded-xl border p-4 ${tier.cls}`} data-testid="service-area-result">
                <tier.Icon className="mt-0.5 h-5 w-5 shrink-0" />
                <p className="text-sm leading-relaxed">{result.message}</p>
              </div>
            )}

            <Link
              to="/request-a-quote"
              data-testid="service-area-quote-btn"
              className="mt-6 inline-flex w-full items-center justify-center rounded-lg border border-[#2e2e39] py-3 text-sm font-bold text-white hover:border-amber-400/60"
            >
              Request a Quote
            </Link>
          </div>
        </div>
      </section>

      <CtaBand title="Not sure if you're in range?" subtitle="Send a quote request with your address and we'll confirm coverage for your project." />
    </>
  );
}
